import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/common/LogoutModal';
import { useSelector, useDispatch } from 'react-redux';
import { BackIcon, MobileMenuIcon, StarrIcon } from '../assets/svgs/svgs';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';
import { logoutUser } from '../store/actions/authActions';

const TopicDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { topic } = location.state || {};

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const user = useSelector((state) => state.auth.user);

  const getKeywordsArray = () => {
    const keywords = topic?.keywords;
    if (!keywords) return [];
    if (Array.isArray(keywords)) return keywords;
    return [
      keywords.main,
      ...(keywords.supporting || []),
      ...(keywords.autocomplete || [])
    ].filter(Boolean);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  if (!topic) {
    return (
      <div className="h-screen flex overflow-hidden bg-main">
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activePage="topic-library"
          onLogout={handleLogout}
        />
        <div className="flex-1 flex flex-col h-screen overflow-hidden">
          <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <MobileMenuIcon/>
                </button>
                <button
                  onClick={() => navigate('/topic-library')}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <BackIcon/>
                  <span className="font-medium text-sm">Back</span>
                </button>
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                  {getUserInitials(user)}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-hidden p-6 flex flex-col items-center justify-center">
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
              <h2 className="text-xl font-bold text-black mb-2">Topic Not Found</h2>
              <p className="text-secondary mb-4">The topic you're looking for doesn't exist or was not passed correctly.</p>
              <button
                onClick={() => navigate('/topic-library')}
                className="px-6 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                Go to Topic Library
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-main">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="topic-library"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MobileMenuIcon/>
              </button>

              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <BackIcon/>
                <span className="font-medium text-sm">Back</span>
              </button>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden p-6 flex flex-col">
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-primary">Topic Details</h1>
          </div>

          <div className="bg-white rounded-2xl flex-1 p-8 overflow-y-auto shadow-sm">
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-black mb-2">
                {topic?.title || 'Untitled Topic'}
              </h2>

              <p className="text-sm text-secondary leading-relaxed mb-4">
                {topic?.description || 'No description available'}
              </p>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-black">Region & Category</span>
                  <div className="flex items-center text-sm font-semibold text-black">
                    <StarrIcon/>
                    {topic?.score || 0}/10 Score
                  </div>
                </div>
                <div className="flex gap-2">
                  {topic?.region && (
                    <span className="tag-brown">{topic.region}</span>
                  )}
                  {topic?.category && (
                    <span className="tag-brown">{topic.category}</span>
                  )}
                </div>
              </div>

              {getKeywordsArray().length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Keywords</h4>
                  <div className="flex gap-2 flex-wrap">
                    {getKeywordsArray().map((keyword, index) => (
                      <span key={index} className="tag-brown">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {topic?.trend && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Trend</h4>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {topic.trend}
                  </span>
                </div>
              )}

              {topic?.suggestedHook && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Suggested Hook</h4>
                  <p className="text-sm text-secondary leading-relaxed">{topic.suggestedHook}</p>
                </div>
              )}

              {topic?.competitorVideos && Array.isArray(topic.competitorVideos) && topic.competitorVideos.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Competitor Videos</h4>
                  <div className="space-y-3">
                    {topic.competitorVideos.map((video, index) => (
                      <div key={video._id || index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-black">{video.creator || 'Unknown Creator'}</p>
                          {video.views && (
                            <span className="text-xs text-secondary whitespace-nowrap">
                              {typeof video.views === 'number'
                                ? video.views.toLocaleString()
                                : video.views} views
                            </span>
                          )}
                        </div>
                        {video.videoLink && (
                          <a
                            href={video.videoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline break-all"
                          >
                            {video.videoLink}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {topic?.geoTrend && Array.isArray(topic.geoTrend) && topic.geoTrend.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Geo Trend</h4>
                  <div className="flex gap-2 flex-wrap">
                    {topic.geoTrend.map((geo, index) => (
                      <div key={geo._id || index} className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                        <p className="text-xs font-medium text-black">{geo.country || 'Unknown'}</p>
                        {geo.rating !== undefined && geo.rating !== null && (
                          <div className="flex items-center gap-1 mt-1">
                            <StarrIcon className="w-3 h-3 text-yellow-500 stroke-current stroke-2" />
                            <span className="text-xs text-secondary">{geo.rating}/5</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {topic?.status && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-black mb-2">Status</h4>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    topic.status === 'available'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {topic.status === 'available' ? 'Available' : 'Claimed'}
                  </span>
                </div>
              )}
            </div>
            <LogoutModal
              showLogoutModal={showLogoutModal}
              onConfirm={confirmLogout}
              onCancel={cancelLogout}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default TopicDetails;
